import {
  CaretLeftIcon,
  CaretRightIcon,
  CornersInIcon,
  CornersOutIcon,
} from '@phosphor-icons/react';
import { Button } from '@shadcn/components/button';
import { Progress } from '@shadcn/components/progress';
import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

interface PresenterControlsProps {
  currentIndex: number;
  totalSlides: number;
  isFullscreen: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onToggleFullscreen: () => void;
}

export default function PresenterControls({
  currentIndex,
  totalSlides,
  isFullscreen,
  onPrevious,
  onNext,
  onToggleFullscreen,
}: PresenterControlsProps) {
  const [visible, setVisible] = useState(true);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const resetHideTimer = useCallback(() => {
    setVisible(true);
    clearTimeout(hideTimerRef.current);
    if (isFullscreen) {
      hideTimerRef.current = setTimeout(() => setVisible(false), 3000);
    }
  }, [isFullscreen]);

  useEffect(() => {
    resetHideTimer();
    return () => clearTimeout(hideTimerRef.current);
  }, [resetHideTimer]);

  // Keyboard navigation
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      switch (e.key) {
        case 'ArrowLeft':
          onPrevious();
          resetHideTimer();
          break;
        case 'ArrowRight':
        case ' ':
          e.preventDefault();
          onNext();
          resetHideTimer();
          break;
        case 'Escape':
          if (isFullscreen) onToggleFullscreen();
          break;
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onPrevious, onNext, onToggleFullscreen, isFullscreen, resetHideTimer]);

  const progress =
    totalSlides > 1 ? (currentIndex / (totalSlides - 1)) * 100 : 100;

  return (
    <ControlsWrapper $visible={visible} onMouseMove={resetHideTimer}>
      <Progress value={progress} className="h-[3px] rounded-none" />
      <BottomBar>
        <Button
          variant="ghost"
          size="icon"
          onClick={onPrevious}
          disabled={currentIndex === 0}
        >
          <CaretLeftIcon size={24} />
        </Button>
        <SlideCounter>
          {currentIndex + 1} / {totalSlides}
        </SlideCounter>
        <Button
          variant="ghost"
          size="icon"
          onClick={onNext}
          disabled={currentIndex >= totalSlides - 1}
        >
          <CaretRightIcon size={24} />
        </Button>
        <FullscreenButtonWrapper>
          <Button variant="ghost" size="icon" onClick={onToggleFullscreen}>
            {isFullscreen ? (
              <CornersInIcon size={20} />
            ) : (
              <CornersOutIcon size={20} />
            )}
          </Button>
        </FullscreenButtonWrapper>
      </BottomBar>
    </ControlsWrapper>
  );
}

const ControlsWrapper = styled.div<{ $visible: boolean }>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 1;
  }
`;

const BottomBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.95);
  border-top: 1px solid hsl(var(--border));
  position: relative;
`;

const SlideCounter = styled.span`
  font-size: 14px;
  color: hsl(var(--muted-foreground));
  min-width: 60px;
  text-align: center;
`;

const FullscreenButtonWrapper = styled.div`
  position: absolute;
  right: 16px;
`;
